import React from 'react';
import clsx from 'clsx';
import find from 'lodash/find';
import get from 'lodash/get';
import includes from 'lodash/includes';
import { getDefaultValue, getScoreValue } from '../../utils/utils';
import { getFormattedAverage } from '../../../../../../utils';
import {
  WIDGET_TYPE_SRL_ACADEMICS_CHART,
  WIDGET_TYPE_SRL_CHALLENGES_CHART,
  WIDGET_TYPE_SRL_GOAL_CHART
} from '../../../../../../constants';

import s from './Column.module.css';

const FREQUENCY_NUMBER_ALIASES = {
  1: '1 - Never',
  2: '2 - Rarely',
  3: '3 - Sometimes',
  4: '4 - Often',
  5: '5 - Always',
};

const FREQUENCY_SUFFIX_ALIASES = {
  1: '1 - Never',
  2: '2 - Very few',
  3: '3 - Some',
  4: '4 - Most',
  5: '5 - Every',
};

const TRACK_ALIASES = {
  yes: 'On track',
  no: 'Off track'
};

const emphasizeSubString = (term, string = '') => {
  if (typeof term !== 'string' || typeof string !== 'string') return string;
  if (!term.trim() || !string.trim()) return string;

  const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedTerm})`, 'gi');

  return string.split(regex).map((part, index) =>
    (part.toLowerCase() === term.toLowerCase() ? <strong key={index}>{part}</strong> : part));
};

export const getCellAlias = ({
  formattedCellValue,
  TABLE_CONSTANTS,
  columnIndex,
  isSRLEnglishVersion,
  searchResponseTerm,
  selectedTags,
}) => {
  const textAlias = TABLE_CONSTANTS.TEXT_ALIAS || {};
  let cellAlias = textAlias[formattedCellValue] ?? formattedCellValue;
  let cellClasses = '';
  if (cellAlias !== '--') {
    if (TABLE_CONSTANTS.HOURS_COLUMN_IDXS && TABLE_CONSTANTS.HOURS_COLUMN_IDXS.includes(columnIndex)) {
      cellAlias = `${getFormattedAverage(parseFloat(formattedCellValue), 2)} h`;
    }
    const isFrequencyColumn = get(TABLE_CONSTANTS, `FREQUENCY_COLUMN_INDICES[${isSRLEnglishVersion}]`, []).includes(columnIndex);
    if (isFrequencyColumn) {
      cellClasses += ` ${s.scoreCell}`;
      cellAlias = FREQUENCY_NUMBER_ALIASES[formattedCellValue] ?? cellAlias;
    }

    const suffixIndex = Number(+formattedCellValue !== 5);
    const suffix = get(TABLE_CONSTANTS, `FREQUENCY_SUFFIX_INDICES[${isSRLEnglishVersion}].SUFFIX[${columnIndex}][${suffixIndex}]`);
    if (suffix || isFrequencyColumn) {
      const frequencyAlias = FREQUENCY_SUFFIX_ALIASES[Number(formattedCellValue)];
      cellAlias = isFrequencyColumn || Number(+formattedCellValue) === 1
        ? FREQUENCY_NUMBER_ALIASES[Number(formattedCellValue)]
        : `${frequencyAlias} ${suffix}`;
    }

    if (isFrequencyColumn || suffix) {
      if (Number(formattedCellValue) === 1) {
        cellClasses += ` ${s.criticalScore}`;
      }
      if (Number(formattedCellValue) === 2) {
        cellClasses += ` ${s.lowScore}`;
      }
    }
  }

  if (TABLE_CONSTANTS.TAGS_COLUMN_IDX === columnIndex) {
    if (Array.isArray(formattedCellValue)) {
      cellClasses += ` ${s.tagsCell}`;
      cellAlias = (
        formattedCellValue.map((val, i) => (
          <div key={i} className={clsx([s.tag, includes(selectedTags, val) && s.tagSelected])}>
            {val}
          </div>
        ))
      );
    } else if (cellAlias !== '--' && TABLE_CONSTANTS.CUSTOM_EMPTY_FIELDS) {
      const columnInfo = find(TABLE_CONSTANTS.CUSTOM_EMPTY_FIELDS, { COLUMN_INDEX: columnIndex });
      if (cellAlias === columnInfo.DEFAULT_VALUE) {
        cellClasses += ` ${s.noChallenges}`;
      }
    }
  }

  if (searchResponseTerm) {
    cellAlias = emphasizeSubString(searchResponseTerm, cellAlias);
  }

  return { cellAlias, cellClasses };
};

function Column({
    columnValue,
    columnIndex,
    constants: TABLE_CONSTANTS,
    centerText = false,
    widgetType,
    isSRLIntroSurvey,
    isSRLEnglishVersion,
    searchResponseTerm,
    selectedTags,
  }) {
  if (get(TABLE_CONSTANTS, `ALLOW_DISPLAY_EMPTY_COLUMNS_INDICES`, []).includes(columnIndex)) {
    return null;
  }
  const columnValueWithDefault = columnValue || getDefaultValue(TABLE_CONSTANTS, columnIndex);
  let formattedCellValue = columnValue === 0 ? columnValue : columnValueWithDefault;
  const scoreColumns = TABLE_CONSTANTS.WARNING_SCORE_COLUMNS_INDICES || [];
  const wordColumns = TABLE_CONSTANTS.WARNING_TEXT_COLUMNS_INDICES || [];
  const highlightColumns = TABLE_CONSTANTS.HIGHLIGHT_TEXT_COLUMNS_INDICES || [];
  const responsesColumnsIndices = TABLE_CONSTANTS.RESPONSES_COLUMN_IDX || [];
  const responsesColumnsIndicesArray = Array.isArray(responsesColumnsIndices)
      ? responsesColumnsIndices
      : [responsesColumnsIndices];
  const isResponsesColumn = responsesColumnsIndicesArray.includes(columnIndex);
  let scoreClasses = '';
  let className = '';
  if (scoreColumns.includes(columnIndex)) {
    const rawScore = parseInt(columnValue, 10);
    scoreClasses += ` ${s.scoreCell}`;
    const criticalScore = getScoreValue(TABLE_CONSTANTS, 'CRITICAL_SCORE_VALUE', columnIndex);
    const lowerScore = getScoreValue(TABLE_CONSTANTS, 'LOW_SCORE_VALUE', columnIndex);
    if (!isNaN(criticalScore) && rawScore <= criticalScore) {
      scoreClasses += ` ${s.criticalScore}`;
    } else if (!isNaN(lowerScore) && rawScore <= lowerScore) {
      scoreClasses += ` ${s.lowScore}`;
    }
  }
  if (wordColumns.includes(columnIndex) && widgetType !== WIDGET_TYPE_SRL_CHALLENGES_CHART) {
    const answerText = formattedCellValue.toUpperCase();
    scoreClasses += ` ${s.scoreCell}`;
    const hasCriticalScoreText =
      TABLE_CONSTANTS.CRITICAL_TEXT_VALUES &&
      TABLE_CONSTANTS.CRITICAL_TEXT_VALUES.includes(answerText);
    const hasLowScoreText =
      TABLE_CONSTANTS.LOW_TEXT_VALUES &&
      TABLE_CONSTANTS.LOW_TEXT_VALUES.includes(answerText);

    if (hasCriticalScoreText) {
      scoreClasses += ` ${s.criticalScore}`;
    }
    if (hasLowScoreText) {
      scoreClasses += ` ${s.lowScore}`;
    }
    if (widgetType === WIDGET_TYPE_SRL_GOAL_CHART && !isSRLIntroSurvey) {
      formattedCellValue = TRACK_ALIASES[formattedCellValue.toLowerCase()];
    }
    if (
      widgetType === WIDGET_TYPE_SRL_ACADEMICS_CHART &&
      !isSRLIntroSurvey &&
      !hasLowScoreText &&
      !hasCriticalScoreText
    ) {
      scoreClasses += ` ${s.noWrapText}`;
    }
  }
  if (highlightColumns.includes(columnIndex)) {
    scoreClasses = '';
    const answerText = formattedCellValue.toUpperCase();
    if (TABLE_CONSTANTS.HIGHLIGHT_TEXT_VALUES && TABLE_CONSTANTS.HIGHLIGHT_TEXT_VALUES.includes(answerText)) {
      scoreClasses += ` ${s.criticalHighlight}`;
    }
  }

  if (isResponsesColumn) {
    className += ` ${s.responseColumn} `;
    if (!columnValue &&
      TABLE_CONSTANTS.CUSTOM_EMPTY_FIELDS &&
      find(TABLE_CONSTANTS.CUSTOM_EMPTY_FIELDS, { COLUMN_INDEX: columnIndex })
    ) {
      className += ` ${s.emptyResponse}`;
    }
  }

  const { cellAlias, cellClasses } = getCellAlias({
    formattedCellValue,
    TABLE_CONSTANTS,
    columnIndex,
    isSRLEnglishVersion,
    searchResponseTerm,
    selectedTags,
  });

  return (
    <div className={clsx(s.column, { [s.centerText]: centerText })}>
      <div className={clsx(className, scoreClasses, cellClasses)}>
        {cellAlias}
      </div>
    </div>
  );
}

export default Column;
